import React, { useState, memo, useMemo, useEffect, useRef, useCallback } from 'react';
import { fftSignals } from './constants';
import { apiCallStatus } from 'helpers/constants';
import { debounce } from 'lodash';
import TimestampGroup from './TimestampGroup';
import { formatDateTime, normalizeUtcTimestamp } from 'helpers/dateTimeFormatter';

const CustomLegend = (props) => {
    const {
        chartInstance,
        selectedTimestamps,
        setSelectedTimestamps,
        getFFTDataByTimestamp,
        spectrumRangeSelection,
        selectedOrganization,
        selectedAsset,
        selectedLegends,
        setSelectedLegends,
        setChartLoader
    } = props;
    const [tick, setTick] = useState(0);
    const selectedLegendsRef = useRef(selectedLegends);
    const signalQueueRef = useRef(fftSignals.Axial);

    useEffect(() => {
        selectedLegendsRef.current = selectedLegends;
    }, [selectedLegends]);

    const updateSelectedLegends = (timestamp, updates) => {
        const newSelectedLegends = {
            ...selectedLegendsRef.current,
            [timestamp]: {
                ...selectedLegendsRef.current[timestamp],
                ...updates
            }
        };
        selectedLegendsRef.current = newSelectedLegends;
        setSelectedLegends(newSelectedLegends);
    };

    useEffect(() => {
        setTimeout(() => setTick(t => t + 1), 200);
    }, [spectrumRangeSelection, selectedTimestamps, chartInstance]);

    const timestampGroups = useMemo(() => {
        const groups = {};
        chartInstance?.series?.forEach(series => {
            if (!series.options?.custom?.isTimestamp) {
                const timestamp = formatDateTime(normalizeUtcTimestamp(series.options.timestamp));
                if (!groups[timestamp]) {
                    groups[timestamp] = [];
                }
                groups[timestamp].push(series);
            }
        });
        return groups;
    }, [tick]);

    const handleLegendsVisible = (targetSeries) => {
        const visibleTimestamps = chartInstance.series.filter(
            s => s.options?.custom?.isTimestamp && s.visible
        );
        const visibleTimestampCount = visibleTimestamps.length;
        if (visibleTimestampCount >= 4 && visibleTimestampCount <= 6) {
            visibleTimestamps.forEach(ts => {
                const signalUpdates = {};
                chartInstance.series.forEach(s => {
                    if (
                        !s.options?.custom?.isTimestamp &&
                        formatDateTime(normalizeUtcTimestamp(s.options.timestamp)) === ts.options.name
                    ) {
                        if (s.name === targetSeries.name) {
                            s.show();
                            signalUpdates[s.name] = true;
                        } else {
                            s.hide();
                            signalUpdates[s.name] = false;
                        }
                    }
                });
                updateSelectedLegends(ts.options.id, signalUpdates);
            });
        } else if (visibleTimestampCount === 3) {
            const visibleSignals = chartInstance.series.filter(
                s =>
                    !s.options?.custom?.isTimestamp &&
                    normalizeUtcTimestamp(s.options.timestamp) === normalizeUtcTimestamp(targetSeries.options.timestamp) &&
                    s.visible
            )?.map(s => s.name) || [];

            let signalToHide = null;

            // Determine which signal to hide based on visible signals count
            if (visibleSignals.length === 2 && !visibleSignals.includes(targetSeries.name)) {
                signalToHide = signalQueueRef?.current || visibleSignals[0];
                const getNextSignalHide = visibleSignals.find(signal => signal !== signalToHide);
                signalQueueRef.current = getNextSignalHide;
            } else if (visibleSignals.length === 1 && !visibleSignals.includes(targetSeries.name)) {
                signalQueueRef.current = visibleSignals[0];
            } else if (visibleSignals.length === 0) {
                signalQueueRef.current = targetSeries.name;
            }

            // Single loop to handle all visibility changes and state updates
            visibleTimestamps.forEach(ts => {
                const signalUpdates = {};
                chartInstance.series.forEach(s => {
                    if (
                        !s.options?.custom?.isTimestamp &&
                        formatDateTime(normalizeUtcTimestamp(s.options.timestamp)) === ts.options.name
                    ) {
                        if (s.name === targetSeries.name) {
                            s.show();
                            signalUpdates[s.name] = true;
                        }
                        else if (
                            visibleSignals.length === 2 && s.name === signalToHide ||
                            visibleSignals.length === 0 && s.name !== targetSeries.name
                        ) {
                            s.hide();
                            signalUpdates[s.name] = false;
                        }
                    }
                });
                updateSelectedLegends(ts.options.id, signalUpdates);
            });
        } else {
            updateSelectedLegends(targetSeries.options.timestamp, {
                [targetSeries.name]: !targetSeries.visible
            });
            if (targetSeries.visible) {
                targetSeries.hide();
            } else {
                targetSeries.show();
            }
        }
    };

    const handleLegendClick = useCallback(debounce((timestamp, axisName) => {

        const targetSeries = chartInstance.series.find(
            s =>
                s.options.timestamp &&
                formatDateTime(normalizeUtcTimestamp(s.options.timestamp)) === timestamp &&
                s.name === axisName
        );

        // Unhide timestamp if any signal is unhidden
        if (!targetSeries.visible) {
            const timestampLegend = chartInstance.series.find(
                s => s.options?.custom?.isTimestamp &&
                    formatDateTime(normalizeUtcTimestamp(targetSeries.options.timestamp)) === s.options.name
            );
            if (timestampLegend && !timestampLegend.visible) {
                timestampLegend.show();
                updateSelectedLegends(targetSeries.options.timestamp, { visible: true });
            }
        }
        const willBeVisible = !targetSeries.visible;
        updateSelectedLegends(targetSeries.options.timestamp, {
            [axisName]: willBeVisible
        });

        // Force update after chart processes the change
        setTimeout(() => setTick(t => t + 1), 0);

        if (spectrumRangeSelection === fftSignals.Piezo) {
            if (targetSeries.visible) {
                targetSeries.hide();
            } else {
                targetSeries.show();
            }
        } else {
            handleLegendsVisible(targetSeries);
        }

        // Force update after chart processes the change
        setTimeout(() => setTick(t => t + 1), 0);

    }, 1000), [selectedTimestamps, spectrumRangeSelection, chartInstance, selectedLegendsRef]);

    const handleTimestampLegendClick = (timestamp) => {
        // Get all signals for this timestamp
        const timestampSignals = chartInstance.series.filter(s =>
            !s.options?.custom?.isTimestamp &&
            formatDateTime(normalizeUtcTimestamp(s.options.timestamp)) === timestamp
        );

        const signalUpdates = {};
        timestampSignals.forEach(s => {
            signalUpdates[s.name] = false;
            s.hide();
        });

        const timestampLegend = chartInstance.series.find(
            s => s.options?.custom?.isTimestamp &&
                s.options.name === timestamp
        );
        const utcTimestamp = timestampLegend?.options?.id;
        if (timestampLegend && timestampLegend.visible) {
            timestampLegend.hide();
            const newSelectedLegends = {
                ...selectedLegendsRef.current,
                [utcTimestamp]: {
                    ...selectedLegendsRef.current[utcTimestamp],
                    visible: false,
                    ...signalUpdates
                }
            };
            selectedLegendsRef.current = newSelectedLegends;
            setSelectedLegends(newSelectedLegends);
        }

        // Force update after chart processes the change
        setTimeout(() => setTick(t => t + 1), 0);

        const visibleTimestamps = chartInstance.series.filter(
            s => s.options?.custom?.isTimestamp && s.visible
        );
        if (visibleTimestamps.length <= 2) {
            // If only one or two timestamps are visible, show all signals for those timestamps
            visibleTimestamps.forEach(ts => {
                const signalsToShow = {};
                chartInstance.series.forEach(s => {
                    if (
                        !s.options?.custom?.isTimestamp &&
                        formatDateTime(normalizeUtcTimestamp(s.options.timestamp)) === ts.options.name
                    ) {
                        s.show();
                        signalsToShow[s.name] = true; // Show all signals
                    }
                });
                updateSelectedLegends(ts.options.id, signalsToShow);
            });
        }
    };

    const handleRemoveTimestamp = (timestamp) => {
        const newTimestamps = selectedTimestamps.filter(
            t => formatDateTime(normalizeUtcTimestamp(t)) !== timestamp
        );

        setSelectedTimestamps(newTimestamps, selectedAsset, selectedOrganization);
        getFFTDataByTimestamp(newTimestamps);

        //Reset selected legends for removing timestamp
        selectedLegendsRef.current = {};
        setSelectedLegends({});
    };

    // Memoize visible Timestamp and Signals Count
    const visibleTimestampCount = useMemo(() => {
        return chartInstance?.series?.filter(
            s => s.options?.custom?.isTimestamp && s.visible
        ).length;
    }, [tick]);

    const visibleSignalsCount = useMemo(() => {
        return chartInstance?.series?.filter(
            s => !s.options?.custom?.isTimestamp && s.visible
        ).length;
    }, [tick]);

    const handleSignalClick = useCallback((signal, timestamp) => {
        if (!signal.visible || visibleSignalsCount > 1) {
            setChartLoader(apiCallStatus.LOADING);
            handleLegendClick(timestamp, signal.name);
        }
    }, [visibleSignalsCount, setChartLoader, handleLegendClick]);

    const memoizedLegend = useMemo(() =>
        <div className='fft-custom-legend'>
            {Object.entries(timestampGroups).map(([timestamp, signals]) =>
                <TimestampGroup
                    key={timestamp}
                    timestamp={timestamp}
                    signals={signals}
                    visibleTimestampCount={visibleTimestampCount}
                    selectedTimestamps={selectedTimestamps}
                    handleTimestampLegendClick={handleTimestampLegendClick}
                    handleSignalClick={handleSignalClick}
                    handleRemoveTimestamp={handleRemoveTimestamp}
                />
            )}
        </div>
        , [
            timestampGroups,
            visibleTimestampCount,
            visibleSignalsCount,
            selectedTimestamps,
            handleTimestampLegendClick,
            handleSignalClick,
            handleRemoveTimestamp
        ]);

    return memoizedLegend;
};

export default memo(CustomLegend);
